import { UserRoleName } from '@brands/services/identity/types/UserProfile';

/**
 * Roles that reach admin-scoped endpoints under `/admin/...`. Services that
 * branch by role (e.g. one_reports) import this rather than redefining it,
 * so adding a future admin tier is a single-file change.
 */
export const ADMIN_SCOPED_ROLES: ReadonlySet<string> = new Set([
  UserRoleName.Admin,
  UserRoleName.SuperAdmin,
  UserRoleName.OrganizationAdmin,
]);

/**
 * Care Advisor and Collector use parallel API namespaces:
 * `/care_advisor/...` and `/collector/...` (same relative paths after the prefix).
 * Other roles use the shared (unprefixed) routes unless a service uses `/admin`, `/patients`, etc.
 */
export function getStaffScopedApiPrefix(
  userRole: string | undefined,
): '' | '/care_advisor' | '/collector' | '/provider' {
  if (userRole === UserRoleName.CareAdvisor) return '/care_advisor';
  if (userRole === UserRoleName.Collector) return '/collector';
  if (userRole === UserRoleName.Provider) return '/provider';
  return '';
}

/**
 * For user-management endpoints that live under /care_advisor for CA roles and /admin for everything else.
 */
export function getAdminOrCareAdvisorPrefix(userRole?: string): '/admin' | '/care_advisor' {
  return userRole === UserRoleName.CareAdvisor ? '/care_advisor' : '/admin';
}

/**
 * For modules that only exist on CA/Collector scoped routers; legacy callers omit role → /care_advisor.
 */
export function getStaffScopedApiPrefixOrCareAdvisor(userRole?: string): '/care_advisor' | '/collector' | '/provider' {
  return getStaffScopedApiPrefix(userRole) || '/care_advisor';
}

function getPersistedAuthRoleName(): string | undefined {
  if (typeof window === 'undefined') return undefined;

  try {
    const persistedRoot = window.localStorage.getItem('persist:root');
    if (!persistedRoot) return undefined;

    const root = JSON.parse(persistedRoot) as { auth?: unknown };
    const auth = typeof root.auth === 'string' ? JSON.parse(root.auth) : root.auth;
    const roleName = (auth as { userInfo?: { role?: { name?: unknown } } } | undefined)?.userInfo?.role?.name;

    return typeof roleName === 'string' ? roleName : undefined;
  } catch {
    return undefined;
  }
}

function getStaffRoleFromPathname(): string | undefined {
  if (typeof window === 'undefined') return undefined;

  const { pathname } = window.location;
  if (pathname === '/collector' || pathname.startsWith('/collector/')) return UserRoleName.Collector;
  if (pathname === '/careadvisor' || pathname.startsWith('/careadvisor/')) return UserRoleName.CareAdvisor;
  if (pathname === '/admin' || pathname.startsWith('/admin/')) return UserRoleName.Admin;

  return undefined;
}

/**
 * Clinical-data has staff-scoped routers for admin, collector, and care advisor.
 * Providers use the shared patient clinical-data route.
 * Prefer the explicit caller role, but infer the current staff role for legacy
 * callers so staff users do not fall back to patient `/clinical-data/*` routes.
 */
export function getClinicalDataApiPrefix(
  userRole?: string,
): '' | '/admin' | '/care_advisor' | '/collector' {
  if (userRole) {
    if (ADMIN_SCOPED_ROLES.has(userRole)) return '/admin';
    if (userRole === UserRoleName.Provider) return '';
    const staffPrefix = getStaffScopedApiPrefix(userRole);
    return staffPrefix === '/care_advisor' || staffPrefix === '/collector' ? staffPrefix : '';
  }

  const inferredRoleName = getPersistedAuthRoleName() || getStaffRoleFromPathname();
  if (inferredRoleName && ADMIN_SCOPED_ROLES.has(inferredRoleName)) return '/admin';
  if (inferredRoleName === UserRoleName.Provider) return '';
  const staffPrefix = getStaffScopedApiPrefix(inferredRoleName);
  if (staffPrefix === '/care_advisor' || staffPrefix === '/collector') return staffPrefix;

  return '';
}
